import { useLazyQuery, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  CurrencyFlagEmoji,
  cn,
  DepositCard,
  Number,
  NumericFormatInput,
  PrimaryFullScreenSpinner,
  TransferCard,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense, useEffect, useMemo } from "react";
import { z } from "zod";
import { getUnitOptions } from "~/components/assetOptions";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { transactionTypeValues } from "~/gql/client/enumValues";
import type {
  CreateLedgerTransactionUnitDetails,
  TransactionType,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";
import { router } from "~/router";

export const Route = createFileRoute("/__auth/transactions_/new")({
  head: () => opsHead("New Transaction"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const formSchema = z.object({
  effectiveAt: z
    .date()
    .transform((d) =>
      DateTime.fromJSDate(d).setZone("UTC", { keepLocalTime: true }).toJSDate(),
    ),
  organisationId: z.string().min(1, "Organisation is required"),
  assetId: z.string().min(1, "Asset is required"),
  direction: z.enum(["CREDIT", "DEBIT"]),
  description: z.string(),
  reference: z.string(),

  unitId: z.string().min(2, "Unit is required"),
  normalAmount: z.number().transform((v) => new Decimal(v)),
  normalUnitPrice: z.number().transform((v) => new Decimal(v)),
  unitAmount: z.number().transform((v) => new Decimal(v)),

  purpose: z.string(),
});

graphql(`
  fragment AssetOptionFields on Asset {
    ... on SavingsAsset {
      product {
        id
        ... on BankAccountProduct {
          currency
        }
      }
    }
    ... on TermDepositAsset {
      product {
        id
        ... on TermDepositProduct {
          currency
        }
      }
    }
    ... on FixedIncomeFundAsset {
      product {
        id
        name
        ... on FundProduct {
          cnav
          latestUnitPrice {
            at
            price
            targetUnit
          }
          unit {
            id
            pricedInCurrency
          }
        }
      }
    }
  }
`);

function View() {
  const { data } = useSuspenseQuery(
    graphql(`
      query OrganisationsWithBusinessEntities {
        organisations {
          id
          name
        }
      }
    `),
  );
  const organisations = (data.organisations ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  const [
    organisationQuery,
    { data: organisationDetails, loading: loadingOrganisation },
  ] = useLazyQuery(
    graphql(`
      query NewTransactionsFormOrganisation($id: ID!) {
        node(id: $id) {
          id
          ... on Organisation {
            id
            businessEntities {
              id
              assets(includeInactive: true) {
                id
                currency
                ...AssetCardFields
                ...AssetOptionFields
              }
            }
          }
        }
      }
    `),
  );

  const [assetQuery, { data: assetData, loading: loadingAsset }] = useLazyQuery(
    graphql(`
      query NewTransactionsFormAsset($id: ID!) {
        node(id: $id) {
          id
          ... on Asset {
            id
            openFundMovements {
              ...TransferCardFields
              ...DepositCardFields
            }
          }
        }
      }
    `),
  );

  const [createTransaction, { loading: createTransactionLoading }] =
    useMutation(
      graphql(`
        mutation CreateTransaction($transaction: CreateLedgerTransactionInput!) {
          createLedgerTransactions(input: {ledgerTransactions: [$transaction]}) {
            transactions {
              id
            }
          }
        }
      `),
    );

  const asset =
    !loadingAsset && assetData?.node && "openFundMovements" in assetData?.node
      ? assetData.node
      : undefined;
  const openFundMovments = (asset?.openFundMovements ?? []).filter(
    (fm) =>
      fm.__typename !== "EburyFXTrade" && fm.__typename !== "FundUnitOrder",
  );

  const assetOptions = useMemo(() => {
    if (
      loadingOrganisation ||
      organisationDetails?.node?.__typename !== "Organisation"
    ) {
      return [];
    }
    return (organisationDetails.node.businessEntities ?? [])
      .flatMap((be) => be.assets ?? [])
      .filter(
        (asset) =>
          asset.class !== "FX" && asset.class !== "EXTERNAL_BANK_ACCOUNT",
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [loadingOrganisation, organisationDetails]);

  const form = useAppForm({
    defaultValues: {
      effectiveAt: new Date(new Date().setHours(0, 0, 0, 0)),
      organisationId: "",
      assetId: "",
      unitAmount: 0,
      normalAmount: 0,
      normalUnitPrice: 0,
      unitId: "",
      direction: "",
      description: "",
      reference: "",
      purpose: "",
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating transaction",
        });
        return;
      }
      const formValues = parsed.data;

      // This form uses a single "purpose" Select that may represent multiple fields in the mutation
      let transactionType: TransactionType | undefined;
      let transferId;
      let payoutId;
      let depositId;

      if (
        transactionTypeValues.includes(formValues.purpose as TransactionType)
      ) {
        transactionType = formValues.purpose as TransactionType;
      } else {
        const fundMovement = openFundMovments.find(
          (f) => f.id === formValues.purpose,
        );
        if (!fundMovement) {
          throw new Error(
            "expected purpose field to be either a transfer or deposit id",
          );
        }
        switch (fundMovement.__typename) {
          case "Transfer":
            transferId = fundMovement.id;
            transactionType =
              fundMovement.sourceAsset?.id === formValues.assetId
                ? "TRANSFER_OUT"
                : "TRANSFER_IN";
            break;
          case "Deposit":
            depositId = fundMovement.id;
            transactionType = "DEPOSIT";
            break;
          default:
            return neverFunc(fundMovement);
        }
      }

      let unitDetails: CreateLedgerTransactionUnitDetails | undefined;
      if (
        !formValues.unitAmount.eq(new Decimal(0)) &&
        !formValues.normalUnitPrice.eq(new Decimal(0))
      ) {
        unitDetails = {
          unitAmount: formValues.unitAmount,
          normalUnitPrice: formValues.normalUnitPrice,
        };
      }

      createTransaction({
        variables: {
          transaction: {
            assetId: formValues.assetId,
            effectiveAt: formValues.effectiveAt,
            direction: formValues.direction,
            unitId: formValues.unitId,
            normalAmount: formValues.normalAmount,
            unitDetails,
            description: formValues.description,
            reference: formValues.reference,
            transactionType,
            transferId,
            payoutId,
            depositId,
          },
        },
        onCompleted: (data) => {
          const transaction = (data.createLedgerTransactions?.transactions ??
            [])[0];
          if (!transaction) {
            throw new Error("expected transaction in mutation payload");
          }
          toast({
            kind: "success",
            autoHide: true,
            title: "Transaction created",
            actions: () => (
              <div className="flex flex-col gap-1">
                <a
                  href={`/ledger/transactions/${transaction.id}`}
                  className="text-primary-interactive text-sm underline hover:text-primary-interactive"
                  onClick={(e) => {
                    e.preventDefault();
                    router.navigate({
                      to: "/ledger/transactions/$id",
                      params: { id: transaction.id },
                    });
                  }}
                >
                  View Transaction
                </a>
                <a
                  href={`/transactions?assetId=${encodeURIComponent(JSON.stringify({ json: [formValues.assetId] }))}`}
                  className="text-primary-interactive text-sm underline hover:text-primary-interactive"
                  onClick={(e) => {
                    e.preventDefault();
                    router.navigate({
                      to: `/transactions?assetId=${encodeURIComponent(JSON.stringify({ json: [formValues.assetId] }))}`,
                    });
                  }}
                >
                  View Asset Transactions
                </a>
              </div>
            ),
          });
          form.reset();
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || "Server error.";
          toast({
            kind: "error",
            autoHide: false,
            title: "Error creating transaction",
            description: errorMessage,
          });
        },
      });
    },
  });

  useEffect(() => {
    if (assetOptions.length === 1) {
      form.setFieldValue("assetId", assetOptions[0].id);
    }
  }, [form.setFieldValue, assetOptions]);

  return (
    <AdminPageContainer testId="admin-transactions-new2">
      <AdminPageHeader
        title="New Transaction"
        subtitle="Create a new transaction"
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="grid w-full items-start gap-6"
      >
        <form.AppForm>
          <form.AppField
            name="organisationId"
            listeners={{
              onChange: ({ value }) => {
                form.resetField("assetId");
                form.resetField("unitId");
                organisationQuery({ variables: { id: value } });
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Select an Organisation"
                placeholder="Choose organisation..."
                errorVisibilityMode="onBlur"
              >
                {organisations.map((org) => (
                  <field.SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.Subscribe
            selector={(state) => ({
              selectedOrganisationId: state.values.organisationId,
              selectedAssetId: state.values.assetId,
            })}
            children={({ selectedOrganisationId, selectedAssetId }) => {
              let placeholder = "Select asset";
              if (loadingOrganisation) {
                placeholder = "Loading assets...";
              } else if (selectedOrganisationId && assetOptions.length === 0) {
                placeholder = "No assets in organisation";
              }

              return (
                <div>
                  <form.AppField
                    name="assetId"
                    listeners={{
                      onChange: ({ value: assetId }) => {
                        form.resetField("purpose");

                        assetQuery({ variables: { id: assetId } });

                        const selectedAsset = assetOptions.find(
                          (asset) => asset.id === assetId,
                        );
                        const unitOptions = selectedAsset
                          ? getUnitOptions({ asset: selectedAsset })
                          : [];
                        if (unitOptions.length === 1) {
                          // NOTE: For some reason this only works on the 2nd and later selections. On the first
                          // selection, something seems to be blanking out the field value after this call.
                          form.setFieldValue("unitId", unitOptions[0].value);
                        } else {
                          form.resetField("unitId");
                        }
                      },
                    }}
                  >
                    {(field) => (
                      <field.SelectField
                        label="Select an Asset"
                        placeholder={placeholder}
                        errorVisibilityMode="onBlur"
                        disabled={
                          !selectedOrganisationId || assetOptions.length === 0
                        }
                      >
                        {assetOptions.map((asset) => (
                          <field.SelectItem key={asset.id} value={asset.id}>
                            <AccountCard
                              subText={[
                                "IDENTIFIER",
                                "CURRENCY",
                                "BUSINESS_ENTITY",
                              ]}
                              account={asset}
                            />
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>

                  {selectedAssetId && (
                    <Link
                      to={`/transactions?assetId=${encodeURIComponent(JSON.stringify({ json: [selectedAssetId] }))}`}
                      className="text-primary-interactive text-xs underline hover:text-primary-interactive"
                    >
                      View Asset Transactions
                    </Link>
                  )}
                </div>
              );
            }}
          />

          <form.Subscribe
            selector={(state) => ({
              selectedAssetId: state.values.assetId,
              selectedPurpose: state.values.purpose,
            })}
            children={({ selectedAssetId, selectedPurpose }) => (
              <div>
                <form.AppField
                  name="purpose"
                  listeners={{
                    onChange: ({ value }) => {
                      const fundMovement = openFundMovments.find(
                        (f) => f.id === value,
                      );
                      if (fundMovement?.__typename === "Deposit") {
                        form.setFieldValue("direction", "CREDIT");
                      } else if (fundMovement?.__typename === "Transfer") {
                        form.setFieldValue(
                          "direction",
                          fundMovement.sourceAsset?.id === selectedAssetId
                            ? "DEBIT"
                            : "CREDIT",
                        );
                      } else {
                        switch (value) {
                          case "DEPOSIT":
                          case "TRANSFER_IN":
                          case "YIELD_REINVESTMENT":
                          case "YIELD":
                            form.setFieldValue("direction", "CREDIT");
                            break;
                          default:
                            form.setFieldValue("direction", "DEBIT");
                        }
                      }
                    },
                  }}
                >
                  {(field) => (
                    <field.SelectField
                      label="Purpose"
                      placeholder={
                        loadingAsset ? "Loading options..." : "Purpose"
                      }
                      errorVisibilityMode="onBlur"
                      disabled={loadingAsset}
                    >
                      {selectedAssetId === asset?.id
                        ? openFundMovments.map((f) => {
                            switch (f.__typename) {
                              case "Transfer": {
                                return (
                                  <field.SelectItem key={f.id} value={f.id}>
                                    <TransferCard
                                      transfer={f}
                                      variant={
                                        f.sourceAsset?.id === asset?.id
                                          ? "TRANSFER_OUT"
                                          : "TRANSFER_IN"
                                      }
                                      subText={["DATE", "PAYMENT_REFERENCE"]}
                                    />
                                  </field.SelectItem>
                                );
                              }
                              case "Deposit": {
                                return (
                                  <field.SelectItem key={f.id} value={f.id}>
                                    <DepositCard deposit={f} />
                                  </field.SelectItem>
                                );
                              }
                            }
                          })
                        : null}
                      <field.SelectItem value={"DEPOSIT"}>
                        New unplanned deposit
                      </field.SelectItem>
                      <field.SelectItem value={"DIRECT_WITHDRAWAL"}>
                        Direct withdrawal
                      </field.SelectItem>
                      <field.SelectItem value={"YIELD"}>Yield</field.SelectItem>
                      <field.SelectItem value={"PROVIDER_FEE"}>
                        Provider fee
                      </field.SelectItem>
                      <field.SelectItem value={"TAX"}>Tax</field.SelectItem>
                      <field.SelectItem value={"YIELD_REMITTANCE"}>
                        Yield remittance
                      </field.SelectItem>
                      <field.SelectItem value={"YIELD_REINVESTMENT"}>
                        Yield reinvestment
                      </field.SelectItem>
                    </field.SelectField>
                  )}
                </form.AppField>

                {(() => {
                  const fundMovement = openFundMovments.find(
                    (f) => f.id === selectedPurpose,
                  );
                  if (fundMovement?.__typename === "Deposit") {
                    return (
                      <Link
                        to={`/deposits/${fundMovement.id}`}
                        className="text-primary-interactive text-xs underline hover:text-primary-interactive"
                      >
                        View Deposit
                      </Link>
                    );
                  } else if (fundMovement?.__typename === "Transfer") {
                    return (
                      <Link
                        to={`/transfers/${fundMovement.id}`}
                        className="text-primary-interactive text-xs underline hover:text-primary-interactive"
                      >
                        View Transfer
                      </Link>
                    );
                  } else {
                    return null;
                  }
                })()}
              </div>
            )}
          />

          <form.AppField name="effectiveAt">
            {(field) => (
              <field.DatePickerField
                className="justify-self-start"
                label="Effective/settlement date"
                calendarProps={{
                  required: true,
                }}
              />
            )}
          </form.AppField>

          <div className="flex gap-4">
            <form.AppField name="direction">
              {(field) => (
                <field.SelectField
                  label="Direction"
                  placeholder="Direction"
                  errorVisibilityMode="onBlur"
                  disabled
                >
                  <field.SelectItem value={"CREDIT"}>Credit</field.SelectItem>
                  <field.SelectItem value={"DEBIT"}>Debit</field.SelectItem>
                </field.SelectField>
              )}
            </form.AppField>

            <form.Subscribe
              selector={(state) => ({
                selectedAssetId: state.values.assetId,
              })}
              children={({ selectedAssetId }) => {
                const selectedAsset = assetOptions.find(
                  (asset) => asset.id === selectedAssetId,
                );
                const unitOptions = selectedAsset
                  ? getUnitOptions({ asset: selectedAsset })
                  : [];

                return (
                  <form.AppField name="unitId">
                    {(field) => (
                      <field.SelectField
                        label="Select unit"
                        placeholder="Select unit"
                        errorVisibilityMode="onBlur"
                        disabled={!selectedAsset || unitOptions.length === 0}
                      >
                        {unitOptions.map((opt) => (
                          <field.SelectItem key={opt.value} value={opt.value}>
                            {opt.name}
                          </field.SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </form.AppField>
                );
              }}
            />
          </div>

          <form.Subscribe
            selector={(state) => ({
              selectedAssetId: state.values.assetId,
            })}
            children={({ selectedAssetId }) => {
              const selectedAsset = assetOptions.find(
                (asset) => asset.id === selectedAssetId,
              );
              return (
                <div className="flex">
                  <form.AppField name="normalAmount">
                    {(field) => (
                      <div>
                        <div className="pb-2">
                          <Txt variant="field-label">Amount</Txt>
                        </div>
                        <NumericFormatInput
                          value={field.state.value}
                          onChange={(v) =>
                            v.target.value &&
                            field.handleChange(v.target.value.toNumber())
                          }
                          required
                          thousandSeparator=","
                          endAdornment={
                            selectedAsset?.currency ? (
                              <span className="mt-1 mb-1 flex items-center gap-2 rounded-sm bg-secondary px-2">
                                <CurrencyFlagEmoji
                                  currency={selectedAsset.currency}
                                />
                                {selectedAsset.currency}
                              </span>
                            ) : null
                          }
                        />
                      </div>
                    )}
                  </form.AppField>
                </div>
              );
            }}
          />

          <form.Subscribe
            selector={(state) => ({
              selectedAssetId: state.values.assetId,
              selectedUnitId: state.values.unitId,
              normalAmount: state.values.normalAmount,
            })}
            children={({ selectedAssetId, selectedUnitId, normalAmount }) => {
              const selectedAsset = assetOptions.find(
                (asset) => asset.id === selectedAssetId,
              );
              const isCnav =
                selectedAsset?.__typename === "FixedIncomeFundAsset" &&
                selectedAsset.product?.__typename === "FundProduct" &&
                selectedAsset.product.cnav === true;
              const showUnitFields =
                selectedUnitId && selectedUnitId !== selectedAsset?.currency;

              if (isCnav && showUnitFields) {
                form.setFieldValue("unitAmount", normalAmount);
                form.setFieldValue("normalUnitPrice", 1);
              }

              return (
                <>
                  {showUnitFields && (
                    <div className="flex flex-row gap-4">
                      <form.AppField name="unitAmount">
                        {(field) => (
                          <div>
                            <div className="pb-2">
                              <Txt variant="field-label">Unit number</Txt>
                            </div>
                            <div className="w-48">
                              <NumericFormatInput
                                value={field.state.value}
                                onChange={(v) =>
                                  v.target.value &&
                                  field.handleChange(v.target.value.toNumber())
                                }
                                thousandSeparator=","
                                required
                                disabled={isCnav}
                              />
                            </div>
                          </div>
                        )}
                      </form.AppField>

                      <div>
                        <form.AppField name="normalUnitPrice">
                          {(field) => (
                            <div>
                              <div className="pb-2">
                                <Txt variant="field-label">Unit price</Txt>
                              </div>
                              <div className="w-64">
                                <NumericFormatInput
                                  value={field.state.value}
                                  onChange={(v) =>
                                    v.target.value &&
                                    field.handleChange(
                                      v.target.value.toNumber(),
                                    )
                                  }
                                  required
                                  disabled={isCnav}
                                  thousandSeparator=","
                                  endAdornment={
                                    selectedAsset?.currency ? (
                                      <span className="mt-1 mb-1 flex items-center gap-2 rounded-sm bg-secondary px-2">
                                        <CurrencyFlagEmoji
                                          currency={selectedAsset.currency}
                                        />
                                        {selectedAsset.currency}
                                      </span>
                                    ) : null
                                  }
                                />
                              </div>
                            </div>
                          )}
                        </form.AppField>

                        <form.Subscribe
                          selector={(state) => ({
                            normalAmount: state.values.normalAmount,
                            unitAmount: state.values.unitAmount,
                            normalUnitPrice: state.values.normalUnitPrice,
                          })}
                          children={({
                            normalAmount,
                            unitAmount,
                            normalUnitPrice,
                          }) => {
                            if (!normalAmount || !unitAmount) {
                              return null;
                            }
                            const expectedNormalUnitPrice = new Decimal(
                              normalAmount,
                            )
                              .div(new Decimal(unitAmount))
                              .toDecimalPlaces(4);
                            const deltaPercentage = expectedNormalUnitPrice
                              .minus(normalUnitPrice)
                              .abs()
                              .div(normalUnitPrice);
                            const showWarning =
                              !!normalUnitPrice &&
                              !new Decimal(normalUnitPrice).eq(
                                expectedNormalUnitPrice,
                              );
                            return (
                              <div className="mt-2">
                                <span className="text-xs">
                                  Expected unit price:{" "}
                                  <span className="font-semibold">
                                    <Number
                                      value={expectedNormalUnitPrice.toNumber()}
                                      format={Number.format.decimal({
                                        digits: 4,
                                      })}
                                    />
                                  </span>
                                  .
                                </span>
                                {showWarning && (
                                  <span className="text-warning text-xs">
                                    &nbsp;Value is{" "}
                                    <Number
                                      value={deltaPercentage.toNumber()}
                                      format={Number.format.percentage({
                                        maximumFractionDigits: 2,
                                      })}
                                    />{" "}
                                    {normalUnitPrice >=
                                    expectedNormalUnitPrice.toNumber()
                                      ? "higher"
                                      : "lower"}{" "}
                                    than expected.
                                  </span>
                                )}
                              </div>
                            );
                          }}
                        />

                        <form.Subscribe
                          selector={(state) => ({
                            normalUnitPrice: state.values.normalUnitPrice,
                          })}
                          children={({ normalUnitPrice }) => {
                            const selectedAsset = assetOptions.find(
                              (asset) => asset.id === selectedAssetId,
                            );
                            const latestUnitPrice =
                              selectedAsset &&
                              selectedAsset.__typename ===
                                "FixedIncomeFundAsset" &&
                              selectedAsset.product &&
                              selectedAsset.product.__typename === "FundProduct"
                                ? selectedAsset.product.latestUnitPrice
                                : null;
                            const delta =
                              !!normalUnitPrice && !!latestUnitPrice
                                ? new Decimal(latestUnitPrice.price)
                                    .minus(new Decimal(normalUnitPrice))
                                    .abs()
                                : null;
                            const deltaPercentage =
                              !!delta && !!latestUnitPrice
                                ? new Decimal(delta).div(latestUnitPrice.price)
                                : null;
                            const showDanger =
                              deltaPercentage && deltaPercentage.gte(0.01);
                            const showWarning =
                              !showDanger &&
                              deltaPercentage &&
                              deltaPercentage.gte(0.005);
                            return (
                              <>
                                {!!latestUnitPrice && (
                                  <div className="mt-2">
                                    <span className="text-xs">
                                      Latest MTM NAV:{" "}
                                      <span className="font-semibold">
                                        <Number
                                          value={latestUnitPrice.price.toNumber()}
                                          format={Number.format.decimal({
                                            digits: 4,
                                          })}
                                        />
                                      </span>
                                      .
                                    </span>
                                    {(showWarning || showDanger) && (
                                      <span
                                        className={cn(
                                          "text-xs",
                                          showWarning
                                            ? "text-warning"
                                            : undefined,
                                          showDanger
                                            ? "text-danger"
                                            : undefined,
                                        )}
                                      >
                                        &nbsp;Value is{" "}
                                        <Number
                                          value={deltaPercentage.toNumber()}
                                          format={Number.format.percentage({})}
                                        />{" "}
                                        {normalUnitPrice >=
                                        latestUnitPrice.price.toNumber()
                                          ? "higher"
                                          : "lower"}{" "}
                                        than MTM NAV.
                                      </span>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            }}
          />

          <form.AppField name="reference">
            {(field) => <field.InputField label="Reference" type="text" />}
          </form.AppField>

          <form.AppField name="description">
            {(field) => <field.TextareaField label="Description" />}
          </form.AppField>

          <div className="mt-10 flex w-full justify-end gap-2">
            <form.SubmitButton
              childrenWhileSubmitting="Creating transaction..."
              disabled={createTransactionLoading}
            >
              Create Transaction
            </form.SubmitButton>
          </div>
        </form.AppForm>
      </form>
    </AdminPageContainer>
  );
}
